import { Navigation, ChevronRight, Check, Plus, Building2, Route } from "lucide-react";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/StatusPill";
import { KIND_LABEL } from "@/lib/calendar";
import { formatDate, daysText } from "@/lib/format";
import { navLinks, isAppleDevice, worstOf } from "@/lib/geo";
import { cn } from "@/lib/utils";

/**
 * LocationSheet – fleire maskiner på same stad (typisk same kunde). Vel ei maskin, eller legg alle i turen.
 */
export const LocationSheet = ({ cluster, open, onOpenChange, selection, onOpenMachine, onToggleSelect, onSelectAll }) => {
  if (!cluster) return null;
  const items = cluster.items;
  const first = items[0];
  const links = navLinks({ lat: cluster.lat, lng: cluster.lng }, first?.customer_name);
  const apple = isAppleDevice();
  const allSelected = items.every((m) => selection?.has(m.id));
  const customers = [...new Set(items.map((m) => m.customer_name).filter(Boolean))];
  const worst = worstOf(items.map((m) => m.overall_status));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="location-sheet">
        <DialogHeader className="min-w-0">
          <div className="flex min-w-0 items-start gap-3 [&>*]:min-w-0">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]">
              <Building2 className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate font-heading text-lg" data-testid="location-sheet-title">{customers.join(", ") || "Same stad"}</DialogTitle>
              <DialogDescription className="truncate">{items.length} maskiner · {first?.position?.address || "—"}</DialogDescription>
            </div>
            <StatusDot status={worst} className="mt-2 h-3 w-3" />
          </div>
        </DialogHeader>
        <DialogBody>
          <ul className="space-y-1.5" data-testid="location-sheet-list">
            {items.map((m) => {
              const sel = selection?.has(m.id);
              return (
                <li key={m.id} className="flex items-center gap-2 rounded-xl border bg-card p-1.5">
                  <button
                    type="button"
                    onClick={() => onToggleSelect?.(m)}
                    aria-pressed={sel}
                    aria-label={sel ? "Fjern frå tur" : "Legg i tur"}
                    className={cn(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-lg border transition-colors",
                      sel ? "border-primary bg-primary text-[#0B0D10]" : "border-border bg-muted text-muted-foreground hover:bg-accent"
                    )}
                    data-testid={`location-sheet-toggle-${m.id}`}
                  >
                    {sel ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => onOpenMachine?.(m)} className="pressable flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 text-left hover:bg-accent" data-testid={`location-sheet-machine-${m.id}`}>
                    <StatusDot status={m.overall_status} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{m.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {m.next ? `${KIND_LABEL[m.next.kind]} · ${formatDate(m.next.date)} · ${daysText(m.next.days_remaining)}` : "Ingen frist i utvalet"}
                        {m.next?.planned_date ? ` · planlagt ${formatDate(m.next.planned_date)}` : ""}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        </DialogBody>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="h-11" onClick={() => onSelectAll?.(items, !allSelected)} data-testid="location-sheet-select-all">
            {allSelected ? <Check className="text-primary" /> : <Route />} {allSelected ? "Alle i turen" : "Legg alle i tur"}
          </Button>
          <Button asChild className="h-11">
            <a href={apple ? links.apple : links.google} target="_blank" rel="noreferrer" data-testid="location-sheet-navigate"><Navigation /> Naviger</a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
